import axios, { AxiosError } from 'axios'

import { makeURL } from '@/api/config'
import { ApiError } from '@/api/errors/ApiError'
import { ServerError } from '@/api/errors/ServerError'

import { SettingDTO } from './dto/SettingDTO'
import { SettingDTOFactory } from './dto/SettingDTOFactory'
import { SettingRaw } from './dto/SettingRaw'
// import jsonDoc from './mocks/settings.json'

export class SettingAPI {
  private routes = {
    getSettings: makeURL('/api/settings'),
  }

  /**
   * Get raw settings directly from the API without transformation.
   *
   * Returns the complete, unprocessed response from the backend including
   * ALL legends and nested structures like:
   * - stations.rainfall (value-based thresholds for rain gauges)
   * - stations.water (state-based thresholds for water level/flow markers)
   *
   * Use this when you need access to legend structures that aren't
   * extracted by SettingDTOFactory (e.g., the unified stations legend).
   *
   * @returns SettingRaw - Complete raw settings from backend
   */
  public async getRawSettings(): Promise<SettingRaw> {
    try {
      const response = await axios.get(this.routes.getSettings)
      return response.data as unknown as SettingRaw
      // return jsonDoc as SettingRaw
    } catch (e) {
      const error = e as AxiosError<ServerError>
      throw ApiError.from(error.response?.data)
    }
  }

  /**
   * Get settings as a processed DTO.
   *
   * Returns transformed settings where only specific legends are extracted
   * (flood_depth, flood_risk, rainfall, return_period, rain_gauges).
   * The nested 'stations' legend structure is NOT included in the DTO.
   *
   * Use this for existing functionality that relies on the Setting model.
   *
   * @returns SettingDTO - Processed settings with selected legends
   */
  public async getSettings(): Promise<SettingDTO> {
    const rawSettings = await this.getRawSettings()
    return SettingDTOFactory.fromRaw(rawSettings)
  }
}
